import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { PremiumDiscountAnalysisPeriod } from '@models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { PricingDistributionDisplayComponent } from '@products/pricing-distribution/pricing-distribution-component.interface';
import { PricingDistributionConfig } from '@products/pricing-distribution/pricing-distribution.config';
import { TranslateService } from '@shared/translate/translate.service';
import {
  BarChartData,
  FundId,
  PricingRangeUpdateValues,
  PricingState,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { PricingService } from '../services/pricing.service';
import cloneDeep from 'lodash/cloneDeep';
import { BarChartComponent } from '@shared/bar-chart/bar-chart.component';
import { SiteConfigService } from '@services/site-config.service';

const logger = Logger.getLogger('PremiumDiscountAnalysisComponent');

export interface PremiumDiscountAnalysisData {
  fundId: FundId;
  periods: PremiumDiscountAnalysisPeriod[];
}

@Component({
  selector: 'ft-premium-discount-analysis',
  templateUrl: './premium-discount-analysis.component.html',
  styleUrls: ['./premium-discount-analysis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PremiumDiscountAnalysisComponent
  extends BaseProductDetailComponent
  implements PricingDistributionDisplayComponent, OnDestroy {
  componentConfiguration: PricingDistributionConfig;
  dataSubscription: Subscription;
  data: PremiumDiscountAnalysisData;
  pricingState: PricingState;
  chart: BarChartData;
  currentPeriod: PremiumDiscountAnalysisPeriod;
  isTraded = true;
  @ViewChild(BarChartComponent, { static: false }) barChart: BarChartComponent;

  constructor(
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private pricingService: PricingService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  populate(
    componentConfig: PricingDistributionConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;

    this.changeDetector.detectChanges();
    if (this.brConfig.fundId) {
      this.isTraded = this.siteConfigService.isTraded(this.brConfig.fundId);
      if (this.isTraded) {
        this.pricingService.setConfigurationName(
          componentConfig.configurationName
        );
        this.dataSubscription = this.pricingService.pricingState$.subscribe(
          (newState: PricingState) => this.mapState(newState)
        );
        this.pricingService.populate(brConfig);
      } else {
        this.changeDetector.detectChanges();
      }
    }
  }

  /**
   * Called when state changes.
   * @param pricingState the new state returned by pricing service
   */
  mapState(pricingState: PricingState): void {
    this.pricingState = cloneDeep(pricingState);

    if (pricingState.isTemit || pricingState.isMartinCurrieGpt) {
      const name: string = this.translateService.instant('products.DISCOUNT');
      this.pricingState.premiumDiscountHistory.highChartOptions.series[0].name = name;
    }

    this.data = this.pricingState.premiumDiscountAnalysis;
    this.currentPeriod = this.data?.periods[0]; // default to first available quarter
    this.setChart();
    this.changeDetector.detectChanges();
  }

  private setChart(): void {
    if (this.currentPeriod) {
      this.chart = {
        fundId: this.data.fundId,
        chartTitle: this.translateService.instant(
          'products.premium-discount-analysis-header'
        ),
        caveatPlacement: 'PremiumDiscountHeader',
        tooltip: this.translateService.tooltip(
          'products.premium-discount-analysis-header'
        ),
        asOfLabel: this.translateService.instant('products.as-of'),
        asOfDate: this.currentPeriod.asOfDate,
        updateFrequency: this.translateService.instant(
          'products.updated-QUARTERLY'
        ),
        proximalTopPlacement: 'PremiumDiscountAnalysisProximalTop',
        proximalBottomPlacement: 'PremiumDiscountAnalysisProximalBottom',
        labelColumnTitle: this.translateService.instant(
          'products.premium-discount-analysis-label-column'
        ),
        breakdownColumnTitle: this.translateService.instant(
          'products.premium-discount-analysis-breakdown-column'
        ),
        dataPoints: this.currentPeriod.dataPoints,
      };
    }
  }

  /**
   * Call pricing service to change price range for chart.
   * This will then update state & price chart, when service provides update.
   */
  updatePremiumDiscountChart(
    pricingChartRangeUpdateValues: PricingRangeUpdateValues
  ): void {
    this.pricingService.createPremiumDiscountChart(
      pricingChartRangeUpdateValues.selectedToggleRange,
      pricingChartRangeUpdateValues.fromDate,
      pricingChartRangeUpdateValues.toDate
    );
  }

  onQuarterChange(period: PremiumDiscountAnalysisPeriod): void {
    logger.debug('Quarter changed', period);
    this.currentPeriod = period;
    this.setChart();
  }

  resizeChart(): void {
    logger.debug('resizeChart() called');
    this.barChart?.resizeChart();
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }
}
